import { useMemo, useState } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, notFound } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { PlusIcon, UsersIcon } from 'lucide-react'

import { Button } from '@ems/ui/components/button'
import { ConfirmDialog } from '@ems/ui/patterns/confirm-dialog'
import { DataTable } from '@ems/ui/patterns/data-table'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { StatusBadge } from '@ems/ui/patterns/status-badge'

import type { Member, Role } from '#admin/features/access/api.ts'
import { AddMemberDialog } from '#admin/features/access/add-member-dialog.tsx'
import { useChangeMemberRole, useRemoveMember } from '#admin/features/access/mutations.ts'
import { membersQuery, rolesQuery } from '#admin/features/access/queries.ts'
import { NotFoundView } from '#admin/components/not-found-view.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import { OptionSelect } from '@ems/ui/patterns/option-select'
import { MembersSkeleton } from '#admin/features/access/access-skeletons.tsx'

/**
 * `/settings/members` — who belongs to this edition, and with which role.
 *
 * **A role change asks first.** Choosing a role opens a confirmation naming the member, the role
 * they have and the one they would get; nothing is sent until it is confirmed. The select keeps
 * showing the role they actually hold throughout, so canceling leaves nothing to undo. The
 * server's refusal (you cannot make somebody an owner; the edition must keep one) arrives as a
 * toast naming the reason.
 */
export const Route = createFileRoute('/_authenticated/settings/members')({
  pendingComponent: MembersSkeleton,
  staticData: { crumb: { parents: [{ label: 'Settings' }], label: 'Members' } },
  loader: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery())
    if (!can(session, 'access.manage')) throw notFound()
    await Promise.all([
      context.queryClient.ensureQueryData(membersQuery()),
      context.queryClient.ensureQueryData(rolesQuery()),
    ])
  },
  // Its own, so the refusal renders in the layout's outlet with the sidebar intact. A thrown
  // not-found goes to the nearest route declaring a component for it, and that route renders it in
  // place of itself — so the layout must not be the one to catch it.
  notFoundComponent: NotFoundView,
  component: MembersPage,
})

function MembersPage() {
  const { data: members } = useSuspenseQuery(membersQuery())
  const { data: roles } = useSuspenseQuery(rolesQuery())
  const changeRole = useChangeMemberRole()
  const remove = useRemoveMember()
  const [removing, setRemoving] = useState<Member | null>(null)
  const [changing, setChanging] = useState<{ member: Member; role: string } | null>(null)

  const roleName = (key: string) => roles.items.find((role) => role.key === key)?.name ?? key

  const columns = useMemo<ColumnDef<Member, never>[]>(
    () => [
      {
        id: 'who',
        header: 'Member',
        meta: { primary: true },
        cell: ({ row }) => (
          <span className="flex flex-col">
            <span className="font-medium">
              {row.original.name}
              {row.original.isSelf ? <span className="text-muted-foreground"> (you)</span> : null}
            </span>
            <span className="text-muted-foreground text-xs">{row.original.email}</span>
          </span>
        ),
      },
      {
        id: 'role',
        header: 'Role',
        cell: ({ row }) => (
          <RoleSelect
            member={row.original}
            roles={roles.items}
            disabled={changeRole.isPending}
            onChange={(role) => setChanging({ member: row.original, role })}
          />
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) =>
          row.original.banned ? (
            // Banned by platform staff. The membership stands, but the login cannot sign in.
            <StatusBadge tone="danger">login banned</StatusBadge>
          ) : (
            <StatusBadge tone="success">active</StatusBadge>
          ),
      },
      {
        id: 'joined',
        header: 'Joined',
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {new Date(row.original.joinedAt).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        meta: { align: 'end' },
        cell: ({ row }) =>
          row.original.isSelf ? null : (
            <Button variant="ghost" size="sm" onClick={() => setRemoving(row.original)}>
              Remove
            </Button>
          ),
      },
    ],
    [roles.items, changeRole.isPending],
  )

  const addMember = (
    <AddMemberDialog roles={roles.items}>
      <Button>
        <PlusIcon />
        Add member
      </Button>
    </AddMemberDialog>
  )

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Members"
        description={`${members.items.length} ${members.items.length === 1 ? 'person' : 'people'} with access to this edition.`}
        actions={addMember}
      />

      <DataTable
        caption="Members of this edition"
        columns={columns}
        data={members.items}
        getRowId={(member) => member.id}
        empty={
          <EmptyState
            icon={<UsersIcon />}
            title="Nobody has access yet"
            description="Add somebody to give them access to this edition."
            action={addMember}
          />
        }
      />

      <ConfirmDialog
        open={changing !== null}
        onOpenChange={(open) => (open ? undefined : setChanging(null))}
        busy={changeRole.isPending}
        title={changing === null ? '' : `Make ${changing.member.email} ${roleName(changing.role)}?`}
        confirmLabel="Change role"
        description={
          changing === null
            ? ''
            : `They are ${roleName(changing.member.role)} today. The new role's access applies on their next request${
                changing.member.isSelf ? ' — including your own, since this is you' : ''
              }.`
        }
        onConfirm={() => {
          if (changing === null) return
          changeRole.mutate(
            { memberId: changing.member.id, role: changing.role },
            { onSettled: () => setChanging(null) },
          )
        }}
      />

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => (open ? undefined : setRemoving(null))}
        destructive
        busy={remove.isPending}
        title={`Remove ${removing?.email ?? ''}?`}
        confirmLabel="Remove"
        description="They lose access to this edition on their next request. Their login, and any other edition they belong to, is untouched."
        onConfirm={() => {
          if (removing === null) return
          remove.mutate(removing.id, { onSettled: () => setRemoving(null) })
        }}
      />
    </div>
  )
}

function RoleSelect({
  member,
  roles,
  disabled,
  onChange,
}: {
  member: Member
  roles: readonly Role[]
  disabled: boolean
  onChange: (role: string) => void
}) {
  // A key whose role was since removed by hand still shows as what it is, not as the first
  // option — which would read as a role the member does not hold.
  const known = roles.some((role) => role.key === member.role)
  return (
    <OptionSelect
      size="sm"
      aria-label={`Role for ${member.email}`}
      value={member.role}
      disabled={disabled}
      onValueChange={(role) => {
        if (role !== member.role) onChange(role)
      }}
      options={[
        ...(known ? [] : [{ value: member.role, label: `${member.role} (no such role)` }]),
        ...roles.map((role) => ({ value: role.key, label: role.name })),
      ]}
    />
  )
}
